import { AsyncAxiosRequestConfig } from 'core/common';
import { AxiosRequestConfig } from 'axios';
import { Nullable } from 'utils/types';
import { axiosInstance } from 'config';
import { ScheduledAccountResponse } from 'features/ActivationClient/shared/Domicile';
import { AddressResponse } from './DeliveryDomicile.model';
import { alphabeticSortLocation } from './helpers';
import { LocationData } from './LocationData.model';

export interface AddressCancelationParamsJSON {
  // client info
  documentNumber: string;
  documentType: string;
  name: string;
  surname: string;
  sex: string;
  // address
  cityId: string;
  street: Nullable<string>;
  streetNumber: Nullable<string>;
  floor?: string;
  flat?: string;
  zipCode: string;
  cpa?: string;
  geographicUnit: Nullable<string>;
  city: Nullable<string>;
  suburb?: Nullable<string>;
  latitude: Nullable<string>;
  longitude: Nullable<string>;
  comments?: string;
  // phone
  areaCode: string;
  phone: string;
  provider: 'XYGO';
  addressDepartment: Nullable<string>;
  transactionType: 'ACTIV' | 'CUST';
}

type SdsSearchParams = {
  account_id: Nullable<number | string>;
  domicile_id?: Nullable<number | string>;
  address_city: Nullable<string>;
  address_flat: Nullable<string>;
  address_floor: Nullable<string>;
  address_number: Nullable<string>;
  address_region: Nullable<string>;
  address_street: Nullable<string>;
  business: Nullable<string>;
  suburb: Nullable<string>;
  tower: Nullable<string>;
};

export type ScheduledAccountParams = {
  domicile_id: Nullable<string>;
  business: string;
};

class Api {
  saveDomicileCancelation(
    bodyParams: AddressCancelationParamsJSON,
    options?: AxiosRequestConfig
  ) {
    return axiosInstance.post(
      '/servicios_fijos/v1/domiciles',
      bodyParams,
      options
    );
  }

  async getSdSResearch(options: AsyncAxiosRequestConfig<SdsSearchParams>) {
    const { data } = await axiosInstance.get(
      '/servicios_fijos/v1/service_requests',
      options
    );
    return data;
  }

  async getScheduledAccount(
    options: AsyncAxiosRequestConfig<ScheduledAccountParams>
  ) {
    const { data } = await axiosInstance.get<ScheduledAccountResponse>(
      '/servicios_fijos/v1/clients/accounts/status',
      options
    );
    return data;
  }

  getRegions(options?: AsyncAxiosRequestConfig<undefined>) {
    return axiosInstance
      .get<{ regions: LocationData[] }>(
        '/servicios_fijos/v1/domiciles/regions',
        options
      )
      .then(({ data }) =>
        data?.regions ? alphabeticSortLocation(data.regions) : []
      );
  }

  getCities(options: AsyncAxiosRequestConfig<{ region: string }>) {
    return axiosInstance
      .get<{ cities: LocationData[] }>(
        '/servicios_fijos/v1/domiciles/cities',
        options
      )
      .then(({ data }) =>
        data?.cities ? alphabeticSortLocation(data.cities) : []
      );
  }

  getSuburbs(
    options: AsyncAxiosRequestConfig<{ region: string; city: string }>
  ) {
    return axiosInstance
      .get<{ suburbs: LocationData[] }>(
        '/servicios_fijos/v1/domiciles/suburbs',
        options
      )
      .then(({ data }) =>
        data?.suburbs ? alphabeticSortLocation(data.suburbs) : []
      );
  }

  getFilteredAddresses(options: AsyncAxiosRequestConfig<any>) {
    const { region, ...restParams } = options.params;

    return axiosInstance
      .get<AddressResponse>(
        `/servicios_fijos/v1/delivery_domiciles/provinces/${region}`,
        { params: restParams }
      )
      .then(({ data }) => data?.deliveryDomiciles || []);
  }
}

export const DomicileAPI = new Api();
